import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';
import { ISite } from '@curbnturf/entities';
import { SiteApiService } from '../backend-api/site-api.service';
import { ListingApiService } from '../backend-api/listing-api.service';
import { dateStringRangeToDateStringArray, getDayOfWeekDaysForNext3Years, getMonthDaysForNext3Years } from '@curbnturf/helpers';
import { AuthService } from '../auth/auth.service';

/** One of the host's own real, bookable sites, with its own blackout config
 *  plus a copy of its parent listing's (inherited per `Site.getBlackoutDays()`
 *  server-side — see objects/src/classes/site.ts). The blockable/bookable
 *  unit everywhere in this app is the *site*, not the listing. */
export interface IHostSite {
  id: number;
  listingId: number;
  listingTitle: string;
  siteName: string;
  blackoutDays: string[];
  blackoutMonths: number[];
  blackoutDayOfWeek: string[];
  listingBlackoutDays: string[];
  listingBlackoutMonths: number[];
  listingBlackoutDayOfWeek: string[];
  /** True when this site is the only one under its listing — a listing-scope
   *  edit and a site-scope edit are equivalent for it today, but listing-scope
   *  is still the forward-compatible choice if more sites get added later. */
  singleSite: boolean;
  available: boolean;
}

interface IHostListingSummary {
  id: number;
  title: string;
  blackoutDays: string[];
  blackoutMonths: number[];
  blackoutDayOfWeek: string[];
}

export type BlackoutScopeItem = { type: 'listing' | 'site'; id: number };

/** Expand every range string in `ranges` into individual ISO days. */
function expandRanges(ranges: string[]): string[] {
  return ranges.reduce<string[]>((days, range) => {
    days.push(...dateStringRangeToDateStringArray(range));
    return days;
  }, []);
}

/** Sorted ISO days → minimal contiguous range strings ('start:end', or
 *  'start:start' for a single day — the server rejects any entry without
 *  a ':'). Inverse of `expandRanges` for a set of bare dates (not months/
 *  day-of-week, which are re-derived, not stored per-day). */
function collapseToBlackoutRanges(days: Iterable<string>): string[] {
  const sorted = [...new Set(days)].sort();
  const ranges: string[] = [];
  let start: string | null = null;
  let prev: string | null = null;

  for (const iso of sorted) {
    if (start === null) {
      start = iso;
      prev = iso;
      continue;
    }
    const isNextDay = DateTime.fromISO(iso).diff(DateTime.fromISO(prev as string), 'days').days === 1;
    if (isNextDay) {
      prev = iso;
      continue;
    }
    ranges.push(`${start}:${prev}`);
    start = iso;
    prev = iso;
  }
  if (start !== null) ranges.push(`${start}:${prev}`);
  return ranges;
}

/** Apply an add/remove of `dates` on top of a blackoutDays array already
 *  in range-string form, re-collapsed back into range strings. */
function applyDateEdit(currentRanges: string[], dates: string[], blocked: boolean): string[] {
  const days = new Set(expandRanges(currentRanges));
  for (const d of dates) {
    if (blocked) days.add(d);
    else days.delete(d);
  }
  return collapseToBlackoutRanges(days);
}

/**
 * Real-backed "which sites/listings does this host own, and what's their
 * blackout config" — mirrors `HostListingsService`'s shape (deep-import the
 * real service, hydrate on auth change, no mock fallback) but at the real
 * per-site granularity, since that's the unit `Site.getBusyDays()` actually
 * enforces against bookings server-side.
 */
@Injectable({ providedIn: 'root' })
export class HostSitesService {
  private auth = inject(AuthService);
  private siteSvc = inject(SiteApiService);
  private listingSvc = inject(ListingApiService);

  private readonly _mySites$ = new BehaviorSubject<IHostSite[]>([]);
  readonly mySites$: Observable<IHostSite[]> = this._mySites$.asObservable();
  private listingsById = new Map<number, IHostListingSummary>();

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user?.id) void this.refreshNow();
      else {
        this._mySites$.next([]);
        this.listingsById.clear();
      }
    });
  }

  async refreshNow(): Promise<void> {
    const userId = this.auth.currentUser?.id;
    if (!userId) {
      this._mySites$.next([]);
      this.listingsById.clear();
      return;
    }
    try {
      // pageSize defaults to 100 — fine for any realistic host today, revisit if that assumption breaks.
      const { sites } = await firstValueFrom(this.siteSvc.fetchAll({ userId }));

      const countByListing = new Map<number, number>();
      for (const s of sites) {
        const listingId = s.listing?.id;
        if (listingId == null) continue;
        countByListing.set(listingId, (countByListing.get(listingId) ?? 0) + 1);
      }

      this.listingsById.clear();
      const mapped: IHostSite[] = [];
      for (const s of sites) {
        const listingId = s.listing?.id;
        if (s.id == null || listingId == null) continue;

        if (!this.listingsById.has(listingId)) {
          this.listingsById.set(listingId, {
            id: listingId,
            title: s.listing?.name || '',
            blackoutDays: s.listing?.blackoutDays ?? [],
            blackoutMonths: s.listing?.blackoutMonths ?? [],
            blackoutDayOfWeek: s.listing?.blackoutDayOfWeek ?? [],
          });
        }

        mapped.push({
          id: s.id,
          listingId,
          listingTitle: s.listing?.name || '',
          siteName: s.name || s.listing?.name || '',
          blackoutDays: s.blackoutDays ?? [],
          blackoutMonths: s.blackoutMonths ?? [],
          blackoutDayOfWeek: s.blackoutDayOfWeek ?? [],
          listingBlackoutDays: s.listing?.blackoutDays ?? [],
          listingBlackoutMonths: s.listing?.blackoutMonths ?? [],
          listingBlackoutDayOfWeek: s.listing?.blackoutDayOfWeek ?? [],
          singleSite: (countByListing.get(listingId) ?? 1) === 1,
          available: s.available !== false,
        });
      }
      this._mySites$.next(mapped);
    } catch (err) {
      console.error('[host-sites] failed to refresh', err);
    }
  }

  list(): IHostSite[] {
    return this._mySites$.value;
  }

  /** Every ISO day this site is blacked out — its own blackoutDays/Months/
   *  DayOfWeek plus its listing's (inherited, same as `Site.getBlackoutDays()`
   *  server-side — reusing the exact same three shared helpers so this can't
   *  drift from what booking validation actually enforces). */
  effectiveBlockedDaysFor(siteId: number): Set<string> {
    const site = this._mySites$.value.find(s => s.id === siteId);
    if (!site) return new Set();

    const days = new Set<string>();
    for (const d of expandRanges(site.blackoutDays)) days.add(d);
    for (const month of site.blackoutMonths) for (const d of getMonthDaysForNext3Years(month)) days.add(d);
    for (const dow of site.blackoutDayOfWeek) for (const d of getDayOfWeekDaysForNext3Years(dow)) days.add(d);
    for (const d of expandRanges(site.listingBlackoutDays)) days.add(d);
    for (const month of site.listingBlackoutMonths) for (const d of getMonthDaysForNext3Years(month)) days.add(d);
    for (const dow of site.listingBlackoutDayOfWeek) for (const d of getDayOfWeekDaysForNext3Years(dow)) days.add(d);
    return days;
  }

  /** Block/unblock `dates` across a mix of listing- and site-scope items.
   *  Each item only ever edits its own row's blackoutDays (a site-scope edit
   *  never touches its listing, and vice versa) — no bulk endpoint exists
   *  server-side, so these run in parallel, one PUT per item. Returns the
   *  items that failed to save instead of letting one failure hide the rest. */
  async setBlackoutBulk(
    scope: BlackoutScopeItem[],
    dates: string[],
    blocked: boolean,
  ): Promise<{ failed: BlackoutScopeItem[] }> {
    const failed: BlackoutScopeItem[] = [];

    await Promise.all(
      scope.map(async item => {
        try {
          if (item.type === 'site') {
            const site = this._mySites$.value.find(s => s.id === item.id);
            if (!site) {
              failed.push(item);
              return;
            }
            const nextDays = applyDateEdit(site.blackoutDays, dates, blocked);
            const updated = await firstValueFrom(this.siteSvc.updateBlackoutTimes(item.id, { blackoutDays: nextDays }));
            this.patchSite(item.id, updated);
          } else {
            const listing = this.listingsById.get(item.id);
            if (!listing) {
              failed.push(item);
              return;
            }
            const nextDays = applyDateEdit(listing.blackoutDays, dates, blocked);
            const updated = await firstValueFrom(this.listingSvc.updateBlackoutTimes(item.id, { blackoutDays: nextDays }));
            this.patchListing(item.id, updated.blackoutDays ?? [], updated.blackoutMonths ?? [], updated.blackoutDayOfWeek ?? []);
          }
        } catch (err) {
          console.error('[host-sites] failed to update blackout', item, err);
          failed.push(item);
        }
      }),
    );

    return { failed };
  }

  private patchSite(siteId: number, updated: ISite): void {
    const blackoutDays = updated.blackoutDays ?? [];
    const blackoutMonths = updated.blackoutMonths ?? [];
    const blackoutDayOfWeek = updated.blackoutDayOfWeek ?? [];
    this._mySites$.next(
      this._mySites$.value.map(s => (s.id === siteId ? { ...s, blackoutDays, blackoutMonths, blackoutDayOfWeek } : s)),
    );
  }

  private patchListing(listingId: number, blackoutDays: string[], blackoutMonths: number[], blackoutDayOfWeek: string[]): void {
    const listing = this.listingsById.get(listingId);
    if (listing) this.listingsById.set(listingId, { ...listing, blackoutDays, blackoutMonths, blackoutDayOfWeek });
    this._mySites$.next(
      this._mySites$.value.map(s =>
        s.listingId === listingId
          ? {
              ...s,
              listingBlackoutDays: blackoutDays,
              listingBlackoutMonths: blackoutMonths,
              listingBlackoutDayOfWeek: blackoutDayOfWeek,
            }
          : s,
      ),
    );
  }
}
